import { z } from '../zod';
import { AttendeeDetailsApiResponseValidationSchema } from '../group-registration/settings';
import { FieldResponseValidationSchema } from '../field/response';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { beta } from '../beta';
import { ThemeOverridesToolResponseValidationSchema } from '../theme/themeOverrides';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { CheckoutAbandonmentResponseValidationSchema } from '../page/common/abandonmentSettings';
import { PageFeeResponseValidationSchema } from '../page/common/fee';
import { PageInvoiceSettingsInputValidationSchema } from '../page/common/invoiceSettings';
import { PageCoverProcessingFeesResponseValidationSchema } from '../page/common/coverProcessingFees';
import { PagePaymentMethodsInputValidationSchema } from '../page/common/paymentMethods';
import { PagePaymentOptionResponseValidationSchema } from '../page/common/paymentOptions';
import { MediaResponseValidationSchema } from '../page/common/file';
import { PageTaxResponseValidationSchema } from '../page/common/tax';
import {
  SharedEventAndFormResponseValidationSchema,
  SharedPageResponseValidationSchema,
} from '../page/get';
import { TicketTypeShowHideLogicResponseValidationSchema } from '../ticket-type/showHideLogic';

export const EventDetailsResponseValidationSchema = z
  .object({
    status: z.enum(['active', 'sold_out', 'canceled']).nullish().openapi({
      description: 'Current event status.',
    }),
    currency: z.string().nullish().openapi({
      description: 'Currency code for ticket pricing (e.g. `usd`).',
    }),
    startDate: z.iso.datetime().nullish().openapi({
      description: 'Event start date in ISO 8601 format.',
    }),
    endDate: z.iso.datetime().nullish().openapi({
      description: 'Event end date in ISO 8601 format.',
    }),
    type: z.enum(['in_person', 'virtual', 'hybrid']).nullish().openapi({
      description: 'Type of event.',
    }),
    timezone: z.string().nullish().openapi({
      description: 'Event timezone identifier (e.g. `America/New_York`).',
    }),
    location: z.string().nullish().openapi({
      description: 'Event location address.',
    }),
    mapsLink: z.string().nullish().openapi({
      description: 'Google Maps or other map URL for the event location.',
    }),
    meetingLink: z.string().nullish().openapi({
      description: 'Virtual meeting link.',
    }),
    platform: z
      .enum([
        'zoom',
        'google_meet',
        'microsoft_teams',
        'youtube',
        'hopin',
        'vimeo',
        'discord',
        'other',
      ])
      .nullish()
      .openapi({
        description: 'Virtual meeting platform.',
      }),
    capacity: z.number().nullish().openapi({
      description: 'Total event capacity. Null means unlimited.',
    }),
    limitRegistrations: z
      .object({
        enabled: z.boolean().nullish(),
        enabledAt: z.string().nullish().openapi({
          description:
            'ISO 8601 timestamp of when duplicate-registration prevention was switched on.',
        }),
      })
      .nullish()
      .openapi({
        description: 'Duplicate-registration prevention settings for this event.',
      }),
    attendeeDetails: beta(AttendeeDetailsApiResponseValidationSchema.nullish()),
    taxBehavior: z.enum(['inclusive', 'exclusive', 'automatic', '']).nullish().openapi({
      description: 'Tax behaviour for this event.',
    }),
    taxCode: z.string().nullish().openapi({
      description: 'Stripe tax code for this event.',
    }),
    fixedTaxRateIds: z.array(z.string()).max(1).nullish().openapi({
      description:
        'List of Tax Rate IDs applied to this event. Empty when tax mode is "none" or "stripe", or when "fixed" with no rate configured.',
    }),
    pdfTicketsEnabled: z.boolean().nullish().openapi({
      description: 'Whether PDF tickets are generated and sent to attendees after purchase.',
    }),
    discounts: z
      .array(
        z.object({
          quantityCondition: z.enum(['total_quantity', 'ticket_type_quantity']).nullish().openapi({
            description: 'Discount condition type.',
          }),
          ticketTypeIds: z.array(ObjectIdStringValidationSchema).nullish().openapi({
            description: 'Resolved ticket type IDs this discount applies to.',
          }),
          minQuantity: z.number().nullish().openapi({ description: 'Minimum quantity threshold.' }),
          maxQuantity: z
            .number()
            .nullish()
            .openapi({ description: 'Maximum quantity for this tier.' }),
          percentOff: z.number().nullish().openapi({ description: 'Percentage discount.' }),
          amountOff: z
            .number()
            .nullish()
            .openapi({ description: 'Fixed discount in smallest currency unit.' }),
        }),
      )
      .nullish()
      .openapi({ description: 'Bulk discounts configured for the event.' }),
  })
  .openapi('EventDetailsResponse');

export const TicketTypeResponseValidationSchema = z

  .object({
    id: z.string().openapi({ description: 'Unique identifier for the ticket type.' }),
    ticketGroupId: ObjectIdStringValidationSchema.nullish().openapi({
      description:
        'ID of the ticket group this ticket type belongs to. This moves the ticket from the current group to the new group.',
    }),
    name: z.string().openapi({ description: 'Name of the ticket type.' }),
    description: z.string().nullish().openapi({ description: 'Description of the ticket type.' }),
    reference: z.string().nullish().openapi({ description: 'External reference ID.' }),
    hidden: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether the ticket type is hidden from customers.' }),
    hideWhenSoldOut: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether hidden when sold out.' }),
    hideWhenNotOnSale: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether hidden when not on sale.' }),
    hideWhenScheduled: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether hidden when scheduled for future sale.' }),
    hideWhenUnavailable: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether hidden when unavailable.' }),
    pricing: z.enum(['paid', 'free', 'pwyw']).nullish().openapi({ description: 'Pricing type.' }),
    price: z.number().nullish().openapi({
      description:
        'Price in smallest currency unit (e.g. cents). For `pwyw` tickets this is the minimum amount.',
    }),
    pwywSuggestedPrice: z
      .number()
      .nullish()
      .openapi({ description: 'Suggested amount in smallest currency unit for `pwyw` tickets.' }),
    discountedFromPrice: z
      .number()
      .nullish()
      .openapi({ description: 'Strike-through price in smallest currency unit.' }),
    feeAmount: z.number().nullish().openapi({
      description:
        'Booking fee charged once per ticket of this type, on top of `price`, in the smallest currency unit. Null when the ticket type has no fee.',
    }),
    capacity: z

      .number()
      .nullish()
      .openapi({ description: 'Maximum tickets available. Null means unlimited.' }),
    quantitySold: z.number().nullish().openapi({ description: 'Number of tickets sold.' }),
    minQuantity: z
      .number()
      .nullish()
      .openapi({ description: 'Minimum tickets required per order.' }),
    maxQuantity: z.number().nullish().openapi({ description: 'Maximum tickets per order.' }),
    showAvailableQuantity: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether available quantity is shown to customers.' }),
    showTicketSaleDates: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether sale start/end dates are shown to customers.' }),
    image: MediaResponseValidationSchema.nullish().openapi({
      description: 'Image for the ticket type.',
    }),
    availabilityBehavior: z
      .enum([
        'always_available',
        'date_time_based',
        'after_ticket_sale_ends',
        'after_ticket_sold_out',
        'after_ticket_ends_or_sold_out',
        'until_event_starts',
      ])
      .nullish()
      .openapi({ description: 'Controls when the ticket becomes available for purchase.' }),
    availabilityStatus: z.enum(['available', 'sold_out', 'unavailable']).nullish().openapi({
      description: 'Current availability status.',
    }),
    saleStartOn: z.iso
      .datetime()
      .nullish()
      .openapi({ description: 'Sale start date in ISO 8601 format.' }),
    saleEndOn: z.iso
      .datetime()
      .nullish()
      .openapi({ description: 'Sale end date in ISO 8601 format.' }),
    triggerTicketTypeId: ObjectIdStringValidationSchema.nullish().openapi({
      description: 'ID of the ticket type that triggers this ticket type to become available.',
    }),
    status: z
      .enum(['enabled', 'disabled', 'archived'])
      .nullish()
      .openapi({ description: 'Status of the ticket type.' }),
    showHideLogic: TicketTypeShowHideLogicResponseValidationSchema.nullish(),
  })
  .openapi('TicketTypeResponse');

const TicketGroupLayoutResponseValidationSchema = z
  .object({
    type: z.enum(['list', 'grid', 'accordion']).nullish().openapi({ description: 'Layout type.' }),
    columns: z.number().nullish().openapi({ description: 'Number of columns for grid layout.' }),
    collapse: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether collapsed by default for accordion layout.' }),
    alignment: z
      .enum(['default', 'center', 'stack'])
      .nullish()
      .openapi({ description: 'Content alignment.' }),
    spacing: z
      .enum(['comfortable', 'compact'])
      .nullish()
      .openapi({ description: 'Spacing between ticket types.' }),
    imageSize: z
      .enum(['small', 'medium', 'large', ''])
      .nullish()
      .openapi({ description: 'Size of ticket type images.' }),
    showTicketGroupName: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether the ticket group name is shown.' }),
    showTicketTypeName: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether ticket type names are shown.' }),
    showTicketPrice: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether ticket prices are shown.' }),
    showTicketTotalPrice: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether total price for selected quantity is shown.' }),
    showPwywPlaceholder: z.boolean().nullish().openapi({
      description: 'Whether the amount placeholder is shown in the pay-what-you-want ticket input.',
    }),
    showTicketFee: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether the booking fee is shown under each ticket price.' }),
    showTicketSaleDateTimezone: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether sale date timezone is shown.' }),
  })
  .openapi('TicketGroupLayoutResponse');

const TicketGroupPreselectResponseValidationSchema = z
  .object({
    enabled: z.boolean().nullish().openapi({ description: 'Whether preselection is enabled.' }),
    ticketTypeId: ObjectIdStringValidationSchema.nullish().openapi({
      description: 'ID of the preselected ticket type.',
    }),
    quantity: z.number().nullish().openapi({ description: 'Preselected quantity.' }),
  })
  .openapi('TicketGroupPreselectResponse');

const TicketGroupBulkDiscountResponseValidationSchema = z
  .object({
    minQuantity: z.number().openapi({ description: 'Minimum quantity for this discount tier.' }),
    maxQuantity: z.number().openapi({ description: 'Maximum quantity for this discount tier.' }),
    percentOff: z.number().openapi({ description: 'Percentage discount for this tier.' }),
  })
  .openapi('TicketGroupBulkDiscountResponse');

export const TicketGroupResponseValidationSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique identifier for the ticket group.' }),
    name: z.string().openapi({ description: 'Name of the ticket group.' }),
    description: z.string().nullish().openapi({ description: 'Description of the ticket group.' }),
    status: z
      .enum(['enabled', 'disabled', 'archived'])
      .nullish()
      .openapi({ description: 'Status of the ticket group.' }),
    availabilityStatus: z.enum(['available', 'sold_out', 'unavailable']).nullish().openapi({
      description: 'Current availability status of the ticket group.',
    }),
    reference: z.string().nullish().openapi({ description: 'External reference ID.' }),
    layout: TicketGroupLayoutResponseValidationSchema.nullish().openapi({
      description: 'Display layout configuration.',
    }),
    ticketSelectionType: z.enum(['single', 'multiple', 'quantity']).nullish().openapi({
      description: 'How customers can select tickets.',
    }),
    feeDisplay: z.enum(['included', 'separate']).nullish().openapi({
      description:
        'How the ticket fees in this group are presented: `included` folds each fee into the shown price, `separate` shows it beside the price.',
    }),
    preselect: TicketGroupPreselectResponseValidationSchema.nullish().openapi({
      description: 'Preselection configuration.',
    }),
    capacity: z.number().nullish().openapi({
      description: 'Maximum tickets sold across all types in this group. Null means unlimited.',
    }),
    hidden: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether the ticket group is hidden from customers.' }),
    hideWhenSoldOut: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether hidden when sold out.' }),
    hideWhenNotOnSale: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether hidden when not on sale.' }),
    hideWhenScheduled: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether hidden when scheduled for future sale.' }),
    hideWhenUnavailable: z
      .boolean()
      .nullish()
      .openapi({ description: 'Whether hidden when unavailable.' }),
    bulkDiscounts: z.array(TicketGroupBulkDiscountResponseValidationSchema).nullish().openapi({
      description: 'Bulk discount tiers.',
    }),
    availabilityBehavior: z
      .enum([
        'always_available',
        'date_time_based',
        'after_ticket_sale_ends',
        'after_ticket_sold_out',
        'after_ticket_ends_or_sold_out',
        'until_event_starts',
      ])
      .nullish()
      .openapi({ description: 'Controls when the ticket group becomes available.' }),
    saleStartOn: z.iso
      .datetime()
      .nullish()
      .openapi({ description: 'Sale start date in ISO 8601 format.' }),
    saleEndOn: z.iso
      .datetime()
      .nullish()
      .openapi({ description: 'Sale end date in ISO 8601 format.' }),
    triggerTicketGroupId: ObjectIdStringValidationSchema.nullish().openapi({
      description: 'ID of the ticket group that triggers this group to become available.',
    }),
    ticketTypes: z
      .array(TicketTypeResponseValidationSchema)
      .nullish()
      .openapi({ description: 'Ticket types in this group.' }),
  })
  .openapi('TicketGroupResponse');

export const EventPageResponseValidationSchema = SharedPageResponseValidationSchema.extend(
  SharedEventAndFormResponseValidationSchema.shape,
)
  .extend({
    type: z.literal('event').openapi({
      description: 'Type of page.',
    }),
    eventDetails: EventDetailsResponseValidationSchema.nullish().openapi({
      description: 'Event-specific details.',
    }),
    attendeeQuestions: beta(
      z.array(FieldResponseValidationSchema).optional().openapi({
        description:
          "The questions each attendee is asked besides their name and email address, on events that collect attendee details. Answer them by `id` in a booking's `attendees[].answers`; an answer to any other ID is refused. The name and email address go in `attendees[].name` and `attendees[].email`.",
      }),
    ),
    showConfirmationEventName: z.boolean().nullish().openapi({
      description: 'Whether the event name is shown on the confirmation page.',
    }),
    showConfirmationEventDateTime: z.boolean().nullish().openapi({
      description: 'Whether the event date and time is shown on the confirmation page.',
    }),
    showConfirmationEventLocation: z.boolean().nullish().openapi({
      description: 'Whether the event location is shown on the confirmation page.',
    }),
    showConfirmationEventMeetingLink: z.boolean().nullish().openapi({
      description: 'Whether the virtual meeting link is shown on the confirmation page.',
    }),
    showConfirmationEventTickets: z.boolean().nullish().openapi({
      description: 'Whether purchased tickets are shown on the confirmation page.',
    }),
    showConfirmationAddToCalendar: z.boolean().nullish().openapi({
      description: 'Whether the add to calendar button is shown on the confirmation page.',
    }),
    showConfirmationEventAddOns: z.boolean().nullish().openapi({
      description: 'Whether purchased add-ons are shown on the confirmation page.',
    }),
    ticketGroups: z.array(TicketGroupResponseValidationSchema).nullish().openapi({
      description: 'Ticket groups and their ticket types.',
    }),
    tax: PageTaxResponseValidationSchema.nullish(),
    savePaymentMethod: z.boolean().nullish().openapi({
      description: "Whether to save the customer's payment method after payment.",
    }),
    paymentMethods: PagePaymentMethodsInputValidationSchema.nullish().openapi({
      description: 'Payment method configuration for this page.',
    }),
    paymentOptions: z.array(PagePaymentOptionResponseValidationSchema).nullish().openapi({
      description: 'Payment options presented to customers at checkout.',
    }),
    fees: z.array(PageFeeResponseValidationSchema).nullish().openapi({
      description: 'Extra fees applied at checkout.',
    }),
    coverProcessingFees: PageCoverProcessingFeesResponseValidationSchema,
    allowDynamicPrice: z.boolean().nullish().openapi({
      description: 'Whether the price can be overridden via URL query parameters.',
    }),
    allowDynamicDiscountedFromPrice: z.boolean().nullish().openapi({
      description: 'Whether the compare-at price can be overridden via URL query parameters.',
    }),
    allowDynamicTitle: z.boolean().nullish().openapi({
      description: 'Whether the title can be overridden via URL query parameters.',
    }),
    allowDynamicDescription: z.boolean().nullish().openapi({
      description: 'Whether the description can be overridden via URL query parameters.',
    }),
    allowDynamicLabel: z.boolean().nullish().openapi({
      description: "Whether a price's label can be set dynamically via URL parameters.",
    }),
    allowDynamicPreselectedPrice: z.boolean().nullish().openapi({
      description:
        'Whether a price can be preselected (and revealed if hidden) via URL parameters.',
    }),
    allowDynamicRedirectUrl: z.boolean().nullish().openapi({
      description: 'Whether the redirect URL can be overridden via URL query parameters.',
    }),
    showCouponCodeField: z.boolean().nullish().openapi({
      description: 'Whether to show the coupon code input.',
    }),
    showCouponCodeFieldType: z.enum(['link', 'field']).nullish().openapi({
      description: 'Display type for the coupon code field.',
    }),
    invoiceSettings: PageInvoiceSettingsInputValidationSchema.nullish().openapi({
      description: 'Invoice configuration for manual payment flows.',
    }),
    checkoutAbandonment: CheckoutAbandonmentResponseValidationSchema.nullish().openapi({
      description: 'Checkout abandonment email reminder settings.',
    }),
  })
  .openapi('EventPageResponse');

// Only MCP reads these API payloads. It checks themeOverrides loosely, so a theme property the API starts
// exposing before the MCP worker redeploys can't fail its tools.
export const EventPageMcpPayloadSchema = EventPageResponseValidationSchema.extend({
  themeOverrides: ThemeOverridesToolResponseValidationSchema,
});

export const CreateEventPageResponseValidationSchema = z.object({
  data: EventPageMcpPayloadSchema,
});
export const UpdateEventPageResponseValidationSchema = CreateEventPageResponseValidationSchema;
export const EventPageToolDataValidationSchema = EventPageMcpPayloadSchema.extend({
  hostedUrl: z.url().meta({
    description: 'Public URL where customers can view and book the event.',
    example: 'https://acme.checkoutpage.com/summer-gala',
  }),
  dashboardUrl: DashboardUrlField,
});
export const EventPageToolResponseValidationSchema = z.object({
  data: EventPageToolDataValidationSchema,
});
export type EventPageResponse = z.output<typeof EventPageResponseValidationSchema>;
export type CreateEventPageResponse = z.output<typeof CreateEventPageResponseValidationSchema>;
export type EventPageToolData = z.output<typeof EventPageToolDataValidationSchema>;
export type EventPageToolResponse = z.output<typeof EventPageToolResponseValidationSchema>;
